import { useLanguage } from "@/hooks/useLanguage";
import { reopenWizardItem, WIZARD_ORDER_ROUTE } from "@/lib/wizardCheckout";
import { interpolate } from "@/lib/localizedContent";
import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ShoppingBag, Pencil, Trash2, Package, Sparkles, X, Wallet, Truck } from "lucide-react";
import { Link } from "react-router-dom";
import { CartItem } from "@/types/quiz";
import { useToast } from "@/hooks/use-toast";
import PriceDisplay from "@/components/PriceDisplay";
import { useCurrency } from "@/hooks/useCurrency";
import { resetCurrentOrderSession, getCartItems, setCartItems as saveCartItems } from "@/lib/sessionUtils";
import { useCustomerCredits, calculateCreditRedemptionWithTickets } from "@/hooks/useCustomerCredits";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { ToastAction } from "@/components/ui/toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartDrawer = ({ isOpen, onClose }: CartDrawerProps) => {
  const navigate = useNavigate();
  const { t, isRTL, language, getLocalizedField } = useLanguage();
  const { toast } = useToast();
  const [collectionNamesAr, setCollectionNamesAr] = useState<Record<string, string | null>>({});
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const { setSelectedCountry, getProductAedAmount } = useCurrency();
  const [packagingCategories, setPackagingCategories] = useState<Record<string, string>>({});
  const { user } = useAuth();
  const [collectionShippingCost, setCollectionShippingCost] = useState<number>(0);

  // Use customer credits hook for ticket display
  const { balance_aed: creditBalance, transactions } = useCustomerCredits();

  // Load packaging categories to identify accessories
  useEffect(() => {
    const loadPackagingCategories = async () => {
      const { data, error } = await supabase
        .from("packaging_options")
        .select("id, category")
        .eq("is_active", true);

      if (!error && data) {
        const categories: Record<string, string> = {};
        data.forEach(pkg => {
          if (pkg.category) {
            categories[pkg.id] = pkg.category;
          }
        });
        setPackagingCategories(categories);
      }
    };
    loadPackagingCategories();
  }, []);

  // Fetch collection shipping cost based on user's address country and cart contents
  useEffect(() => {
    const fetchShipping = async () => {
      const selectedItems = cartItems.filter(item => selectedItemIds.has(item.id));
      const hasCollectionItems = selectedItems.some(item => item.isCollectionItem);
      const hasQuizItems = selectedItems.some(item => !item.isCollectionItem);

      // Shipping only applies to collection-only checkouts.
      // If any quiz/personalized items are present, no shipping cost.
      if (!hasCollectionItems || hasQuizItems) {
        setCollectionShippingCost(0);
        return;
      }

      // For logged-in users, get country from saved addresses or profile
      let countryName: string | null = null;
      if (user) {
        try {
          const { data: addresses } = await supabase
            .from('customer_addresses')
            .select('country')
            .eq('user_id', user.id)
            .eq('is_default', true)
            .limit(1)
            .single();
          if (addresses?.country) countryName = addresses.country;
        } catch {
          // No default address
        }
        if (!countryName) {
          // Fallback: try first address
          try {
            const { data: addresses } = await supabase
              .from('customer_addresses')
              .select('country')
              .eq('user_id', user.id)
              .limit(1)
              .single();
            if (addresses?.country) countryName = addresses.country;
          } catch { /* no address */ }
        }
      }

      if (!countryName) {
        const storedQuiz = sessionStorage.getItem('quizData');
        if (storedQuiz) {
          try { countryName = JSON.parse(storedQuiz).country; } catch {}
        }
      }

      if (!countryName) {
        setCollectionShippingCost(0);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('collection_shipping_policies')
          .select('shipping_price_aed, free_shipping_threshold_aed, discount_threshold_aed, discounted_shipping_price_aed, sample_premium_aed, shipping_countries!inner(country_name)')
          .eq('is_active', true);

        if (error || !data) { setCollectionShippingCost(0); return; }

        const policy = data.find((p: any) => {
          const pName = (p as any).shipping_countries?.country_name;
          return pName?.toLowerCase() === countryName?.toLowerCase();
        });

        if (!policy) { setCollectionShippingCost(0); return; }

        const selectedCollectionItems = cartItems
          .filter(item => item.isCollectionItem && selectedItemIds.has(item.id));

        const collectionSubtotal = selectedCollectionItems
          .reduce((sum, item) => sum + (item.price / (item.quantity || 1)) * (item.quantity || 1), 0);

        if (policy.free_shipping_threshold_aed && collectionSubtotal >= policy.free_shipping_threshold_aed) {
          setCollectionShippingCost(0);
          return;
        }

        let baseShipping = policy.shipping_price_aed;
        if (policy.discount_threshold_aed && policy.discounted_shipping_price_aed &&
            collectionSubtotal >= policy.discount_threshold_aed) {
          baseShipping = policy.discounted_shipping_price_aed;
        }

        // Sample premium: only when ALL selected collection items are free samples
        const sampleCount = selectedCollectionItems.filter(item => item.isSample).length;
        const nonSampleCount = selectedCollectionItems.filter(item => !item.isSample).length;
        const samplePremium = (policy as any).sample_premium_aed || 0;

        let sampleSurcharge = 0;
        if (nonSampleCount === 0 && sampleCount > 1 && samplePremium > 0) {
          sampleSurcharge = (sampleCount - 1) * samplePremium;
        }

        setCollectionShippingCost(baseShipping + sampleSurcharge);
      } catch {
        setCollectionShippingCost(0);
      }
    };
    fetchShipping();
  }, [cartItems, selectedItemIds, user]);

  // Sync collection items from database to main cart when drawer opens
  const syncCollectionItems = async () => {
    if (!user) return;

    try {
      const { data: dbItems, error } = await supabase
        .from('collection_cart')
        .select(`
          *,
          product:collection_products(
            id, name, name_ar, slug, price_aed, price_50ml, price_100ml, sample_price_aed, has_sample,
            images:collection_product_images(image_url, is_primary)
          )
        `)
        .eq('user_id', user.id);

      if (error || !dbItems) return;
      setCollectionNamesAr(Object.fromEntries(dbItems.map(item => [item.product_id, item.product?.name_ar || null])));

      const currentCart = getCartItems();

      // First, remove all collection items from main cart (we'll rebuild from DB)
      const nonCollectionItems = currentCart.filter((item: CartItem) => !item.isCollectionItem);

      // Add all collection items from database with correct quantities
      for (const dbItem of dbItems) {
        if (!dbItem.product) continue;

        const primaryImage = (dbItem.product as any).images?.find((img: any) => img.is_primary) || (dbItem.product as any).images?.[0];
        const price = dbItem.is_sample
          ? (dbItem.product as any).sample_price_aed || 0
          : dbItem.size_ml === 50
            ? (dbItem.product as any).price_50ml || (dbItem.product as any).price_aed
            : (dbItem.product as any).price_100ml || (dbItem.product as any).price_aed;

        const newItem: CartItem = {
          id: `collection-${dbItem.id}`,
          type: 'collection',
          recipientName: '',
          country: '',
          quantity: dbItem.quantity,
          price: price * dbItem.quantity,
          isCollectionItem: true,
          collectionProductId: dbItem.product_id,
          collectionProductName: (dbItem.product as any).name,
          collectionProductSlug: (dbItem.product as any).slug,
          collectionProductImage: primaryImage?.image_url || null,
          sizeMl: dbItem.size_ml || 100,
          isSample: dbItem.is_sample || false,
        };
        nonCollectionItems.push(newItem);
      }

      saveCartItems(nonCollectionItems);
      return nonCollectionItems;
    } catch (error) {
      console.error('Error syncing collection cart:', error);
    }
    return null;
  };

  useEffect(() => {
    if (isOpen) {
      // First sync collection items from database, then load cart
      const loadCart = async () => {
        let items: CartItem[] = getCartItems();

        if (user) {
          const syncedCart = await syncCollectionItems();
          if (syncedCart) {
            items = syncedCart;
          }
        }

        setCartItems(items);

        // Check for Buy Now flag — auto-select only that item
        const buyNowId = sessionStorage.getItem("buyNowProductId");
        if (buyNowId) {
          sessionStorage.removeItem("buyNowProductId");
          // Find cart item matching this DB cart id or product_id
          const buyNowItem = items.find(
            item => item.id === `collection-${buyNowId}` ||
                    item.collectionProductId === buyNowId
          );
          if (buyNowItem) {
            setSelectedItemIds(new Set([buyNowItem.id]));
            sessionStorage.setItem("checkoutSelectedItems", JSON.stringify([buyNowItem.id]));
            return; // Skip normal selection restore
          }
          // Fallback: if no exact match found, still don't select all —
          // select nothing and let user choose
        }

        // Restore selection from sessionStorage, or select all by default
        const storedSelection = sessionStorage.getItem("checkoutSelectedItems");
        if (storedSelection) {
          const storedIds = new Set(JSON.parse(storedSelection) as string[]);
          // Only use stored selection if items still exist
          const validIds = new Set(items.filter(item => storedIds.has(item.id)).map(item => item.id));
          // Auto-select any new items not in the stored selection
          items.forEach(item => {
            if (!storedIds.has(item.id)) {
              validIds.add(item.id);
            }
          });
          setSelectedItemIds(validIds);
          // Update sessionStorage with new selection
          sessionStorage.setItem("checkoutSelectedItems", JSON.stringify(Array.from(validIds)));
        } else {
          setSelectedItemIds(new Set(items.map(item => item.id)));
          sessionStorage.setItem("checkoutSelectedItems", JSON.stringify(items.map(item => item.id)));
        }

        // Sync currency display with cart item country
        if (items.length > 0 && items[0].country) {
          const countryMapping: Record<string, string> = {
            "United Arab Emirates": "AE",
            "Saudi Arabia": "SA",
            "United States": "US",
            "United Kingdom": "GB",
            "India": "IN",
            "Pakistan": "PK",
            "Egypt": "EG",
            "Kuwait": "KW",
            "Qatar": "QA",
            "Bahrain": "BH",
            "Oman": "OM",
            "Jordan": "JO",
          };
          const countryCode = countryMapping[items[0].country] || items[0].country;
          setSelectedCountry(countryCode);
        }
      };

      loadCart();
    }
  }, [isOpen, setSelectedCountry, user]);

  const toggleItemSelection = (itemId: string) => {
    setSelectedItemIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      // Persist selection to sessionStorage
      sessionStorage.setItem("checkoutSelectedItems", JSON.stringify(Array.from(newSet)));
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    let newSet: Set<string>;
    if (selectedItemIds.size === cartItems.length) {
      newSet = new Set();
    } else {
      newSet = new Set(cartItems.map(item => item.id));
    }
    // Persist selection to sessionStorage
    sessionStorage.setItem("checkoutSelectedItems", JSON.stringify(Array.from(newSet)));
    setSelectedItemIds(newSet);
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if delete dialog is open
      if (deleteItemId) return;

      // Check if click is inside an alert dialog
      const target = event.target as Node;
      const alertDialog = document.querySelector('[role="alertdialog"]');
      if (alertDialog && alertDialog.contains(target)) return;

      if (drawerRef.current && !drawerRef.current.contains(target)) {
        onClose();
      }
    };

    if (isOpen) {
      // Delay adding listener to prevent immediate close
      setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 100);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, deleteItemId]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleEditItem = (item: CartItem) => {
    if (reopenWizardItem(item)) { onClose(); navigate(WIZARD_ORDER_ROUTE); return; }
    onClose();

    // Determine if this is the first order or a subsequent order
    const itemIndex = cartItems.findIndex((i) => i.id === item.id);
    const isFirstOrder = itemIndex === 0;
    const firstOrderCountry = cartItems.length > 0 ? cartItems[0].country : null;

    // Reset any in-progress order/edit session so each Edit loads the correct item
    resetCurrentOrderSession();

    // Store the item being edited with a unique session key to force reset
    sessionStorage.setItem("editingCartItem", JSON.stringify(item));
    sessionStorage.setItem("editingCartItemId", item.id);
    sessionStorage.setItem("editMode", "true");
    sessionStorage.setItem("editSessionKey", Date.now().toString());

    // Store original perfumes and codes to compare later
    if (item.perfumes && item.perfumes.length > 0) {
      sessionStorage.setItem("originalPerfumes", JSON.stringify(item.perfumes));
      sessionStorage.setItem("perfumes", JSON.stringify(item.perfumes));
    } else if (item.perfume) {
      sessionStorage.setItem("originalPerfumes", JSON.stringify([item.perfume]));
    }

    // Preserve personalization details from the cart item
    if (item.greetingMessage) {
      sessionStorage.setItem("greetingMessage", item.greetingMessage);
    }
    if (item.photoUrl) {
      sessionStorage.setItem("photoPreview", item.photoUrl);
    }

    // Store the selected option (package type) from the cart item
    sessionStorage.setItem("selectedOption", item.type);

    // If single bottle, store the selected perfume
    if (item.type === "single" && item.perfume) {
      sessionStorage.setItem("selectedPerfume", JSON.stringify(item.perfume));
    }

    // Set up quiz data from the cart item - preserve all original quiz responses
    const storedOriginalQuizData = item.quizData;
    const quizData = storedOriginalQuizData || {
      recipientName: item.recipientName,
      country: item.country,
      gender: "unisex",
      ageBracket: "",
      skinType: "",
      nature: "",
      personality: "",
      style: "",
    };
    sessionStorage.setItem("quizData", JSON.stringify(quizData));

    // Store original quiz preferences to compare later
    sessionStorage.setItem(
      "originalQuizPreferences",
      JSON.stringify({
        gender: quizData.gender,
        ageBracket: quizData.ageBracket,
        skinType: quizData.skinType,
        nature: quizData.nature,
        personality: quizData.personality,
        style: quizData.style,
      })
    );

    // Country locking logic:
    // - First order: User CAN change country (no lock)
    // - Second+ order: Country is locked to first order's country
    if (!isFirstOrder && firstOrderCountry) {
      sessionStorage.setItem("editLockedCountry", firstOrderCountry);
      sessionStorage.setItem("quizLockedCountry", firstOrderCountry);
    } else {
      sessionStorage.removeItem("editLockedCountry");
      sessionStorage.removeItem("quizLockedCountry");
    }

    // Reset to step 1 for quiz editing
    sessionStorage.setItem("quizCurrentStep", "0");
    sessionStorage.setItem("returnToQuiz", "true");

    // Enable full step strip navigation during edit
    sessionStorage.setItem("highestPhaseReached", "4");

    // Notify Quiz/QuizForm to re-load the edit target even if the route doesn't change
    window.dispatchEvent(new Event("editSessionUpdated"));

    // Navigate to quiz Step 1
    navigate("/quiz");
  };

  const handleDeleteItem = async (itemId: string) => {
    // Find the item before removing to check if it's a collection item
    const itemToDelete = cartItems.find(item => item.id === itemId);

    // If it's a collection item, delete from DB (no guest flow anymore)
    if (itemToDelete?.isCollectionItem && user) {
      try {
        const dbId = itemId.startsWith('collection-') ? itemId.replace('collection-', '') : null;
        if (dbId) {
          await supabase
            .from('collection_cart')
            .delete()
            .eq('id', dbId)
            .eq('user_id', user.id);
        } else if (itemToDelete.collectionProductId) {
          await supabase
            .from('collection_cart')
            .delete()
            .eq('user_id', user.id)
            .eq('product_id', itemToDelete.collectionProductId)
            .eq('is_sample', itemToDelete.isSample || false);
        }
      } catch (error) {
        console.error('Error removing collection item from database:', error);
      }
    }

    const updatedCart = cartItems.filter(item => item.id !== itemId);
    setCartItems(updatedCart);
    saveCartItems(updatedCart);
    setDeleteItemId(null);

    // Reset session data so quiz is reset for the removed item
    resetCurrentOrderSession();

    // If cart is now empty, also clear continue shopping flag
    if (updatedCart.length === 0) {
      sessionStorage.removeItem("continueShopping");
    }

    // Trigger storage event for header to update
    window.dispatchEvent(new Event("storage"));

    // Dispatch cartUpdated so all useCollectionCart instances re-fetch
    window.dispatchEvent(new Event("cartUpdated"));

    toast({
      title: t("cart.removed"),
      description: t("cart.removedDescription"),
    });
  };

  const getItemUnitPrice = (item: CartItem) => {
    // Collection items store total price (unit price × quantity) directly
    // Return per-unit price for consistency with quantity multiplier
    if (item.isCollectionItem) {
      return item.price / (item.quantity || 1);
    }
    if (item.packageId) {
      return getProductAedAmount(item.packageId, item.price);
    }
    return item.price;
  };

  // Calculate subtotal for selected items
  const getSubtotal = () => {
    return cartItems
      .filter(item => selectedItemIds.has(item.id))
      .reduce((sum, item) => sum + getItemUnitPrice(item) * (item.quantity || 1), 0);
  };

  // Calculate credit redemption for selected items
  const getCreditRedemption = () => {
    // Get available tickets (credits) sorted oldest first for FIFO
    const availableTickets = transactions
      .filter(tx => tx.transaction_type === 'credit')
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
      .map(tx => ({ amount_aed: tx.amount_aed, id: tx.id }));

    if (!availableTickets.length) return 0;

    // Only calculate for selected, non-accessory items
    const selectedItems = cartItems.filter(item => selectedItemIds.has(item.id));
    const eligibleItems = selectedItems
      .map((item, index) => {
        const category = item.packageId ? packagingCategories[item.packageId] : null;
        const isAccessory = category === 'accessory' || !!(item as any).isSample;
        return {
          price: getItemUnitPrice(item) * (item.quantity || 1),
          isAccessory,
          index,
          quantity: item.quantity || 1,
        };
      })
      .filter(item => !item.isAccessory);

    const result = calculateCreditRedemptionWithTickets(availableTickets, eligibleItems);
    return result.totalRedemption;
  };

  // Get total price after credits + shipping
  const getTotalPrice = () => {
    const subtotal = getSubtotal();
    const credits = getCreditRedemption();
    return Math.max(0, subtotal - credits + collectionShippingCost);
  };

  const getSelectedCount = () => selectedItemIds.size;

  const handleCheckout = async () => {
    if (selectedItemIds.size === 0) {
      toast({
        title: t("cart.noneSelected"),
        description: t("cart.selectRequired"),
        variant: "destructive",
      });
      return;
    }

    // Auth gate: require login before checkout
    if (!user) {
      toast({
        title: t("cart.loginRequired"),
        description: t("cart.loginDescription"),
        variant: "destructive",
      });
      onClose();
      navigate("/auth?redirect=/checkout");
      return;
    }

    // Store selected item IDs for checkout page
    sessionStorage.setItem("checkoutSelectedItems", JSON.stringify(Array.from(selectedItemIds)));

    // Check if user has any saved addresses
    try {
      const { data: addresses, error } = await supabase
        .from('customer_addresses')
        .select('id')
        .eq('user_id', user.id)
        .limit(1);

      if (!error && (!addresses || addresses.length === 0)) {
        // No saved addresses — redirect to address page first
        onClose();
        navigate("/shipping-address");
        return;
      }
    } catch {
      // On error, let checkout page handle address validation
    }

    // Navigate to checkout page (address validation + shipping calculation happens there)
    onClose();
    navigate("/checkout");
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] animate-fade-in" />

      {/* Drawer */}
      <div
        ref={drawerRef}
        dir={isRTL ? "rtl" : "ltr"}
        lang={language}
        className="fixed top-0 end-0 h-full w-full max-w-md bg-background border-s border-border z-[70] shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="font-display text-xl text-foreground flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-gold" />
            Your Cart
            {cartItems.length > 0 && (
              <span className="text-sm text-muted-foreground font-normal">
                ({interpolate(t(cartItems.length === 1 ? "cart.itemCount" : "cart.itemsCount"), { count: cartItems.length })})
              </span>
            )}
          </h2>
          <button
            aria-label={t("cart.close")}
            onClick={onClose}
            className="p-2 rounded-full hover:bg-secondary transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col h-[calc(100%-4rem)]">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {cartItems.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingBag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-display text-lg text-foreground mb-2">
                  {t("cart.empty")}
                </h3>
                <p className="text-muted-foreground text-sm mb-6">
                  {t("cart.getStarted")}
                </p>
                <Button
                  variant="gold"
                  onClick={() => {
                    // Force a truly fresh quiz start (no previously entered data)
                    resetCurrentOrderSession();
                    sessionStorage.removeItem("continueShopping");
                    sessionStorage.setItem("startFreshQuiz", "true");
                    onClose();
                    navigate("/quiz");
                  }}
                >
                  {t("cart.create")}
                </Button>
                <Button
                  variant="gold-outline"
                  className="mt-2"
                  onClick={() => {
                    onClose();
                    navigate("/anaya-collection");
                  }}
                >
                  {t("cart.exclusive")}
                </Button>
              </div>
            ) : (
              <>
                {/* Select All */}
                {cartItems.length > 1 && (
                  <div className="flex items-center gap-2 pb-2 border-b border-border">
                    <Checkbox
                      id="select-all"
                      checked={selectedItemIds.size === cartItems.length}
                      onCheckedChange={toggleSelectAll}
                      className="border-gold data-[state=checked]:bg-gold data-[state=checked]:border-gold"
                    />
                    <label htmlFor="select-all" className="text-sm text-muted-foreground cursor-pointer">
                      {interpolate(t("cart.selectAll"), { count: cartItems.length })}
                    </label>
                  </div>
                )}

                {cartItems.map((item, index) => (
                  <div
                    key={item.id}
                    className={`bg-secondary/30 rounded-xl p-4 border transition-all duration-300 animate-fade-in opacity-0 ${
                      selectedItemIds.has(item.id) ? 'border-gold/50' : 'border-border'
                    } hover:border-gold/30`}
                    style={{
                      animationDelay: `${index * 50}ms`,
                      animationFillMode: "forwards"
                    }}
                  >
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      <Checkbox
                        aria-label={t("cart.selectItem")}
                        checked={selectedItemIds.has(item.id)}
                        onCheckedChange={() => toggleItemSelection(item.id)}
                        className="mt-2 border-gold data-[state=checked]:bg-gold data-[state=checked]:border-gold"
                      />

                      {/* Icon / Image */}
                      {item.isCollectionItem && item.collectionProductImage ? (
                        <img
                          src={item.collectionProductImage}
                          alt={getLocalizedField(item.collectionProductName, collectionNamesAr[item.collectionProductId || ""]) || t("cart.product")}
                          className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0">
                          {item.type === "box" ? (
                            <Package className="h-5 w-5 text-gold" />
                          ) : (
                            <Sparkles className="h-5 w-5 text-gold" />
                          )}
                        </div>
                      )}

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <h3 className="font-display text-sm text-foreground truncate">
                            {item.isCollectionItem
                              ? getLocalizedField(item.collectionProductName, collectionNamesAr[item.collectionProductId || ""])
                              : item.type === "box"
                                ? t("cart.signature")
                                : t("cart.single")}
                          </h3>
                        </div>

                        {item.isCollectionItem ? (
                          <p className="text-xs text-muted-foreground mb-2">
                            {item.isSample ? t("cart.sample") : interpolate(t("cart.size"), { size: item.sizeMl || 100 })}
                            {(item.quantity || 1) > 1 && ` × ${item.quantity}`}
                            <span className="ms-1 text-gold">{t("cart.collection")}</span>
                          </p>
                        ) : (
                          <p className="text-xs text-muted-foreground mb-2">
                            {interpolate(t("cart.forCountry"), { name: item.recipientName, country: item.country })}
                          </p>
                        )}

                        {/* Perfume Details - only for personalized items */}
                        {!item.isCollectionItem && item.type === "single" && item.perfume && (
                          <div className="text-xs text-muted-foreground ps-2 border-s-2 border-gold/30">
                            <span className="text-foreground/80">{getLocalizedField(item.perfume.name, (item.perfume as typeof item.perfume & { name_ar?: string }).name_ar)}</span>
                            <span dir="ltr" className="text-gold ms-1">{item.perfume.code}</span>
                          </div>
                        )}

                        {!item.isCollectionItem && item.type === "box" && item.perfumes && (
                          <div className="text-xs text-muted-foreground ps-2 border-s-2 border-gold/30 space-y-0.5">
                            {item.perfumes.slice(0, 2).map((p) => (
                              <div key={p.id}>
                                <span className="text-foreground/80">{getLocalizedField(p.name, (p as typeof p & { name_ar?: string }).name_ar)}</span>
                                <span dir="ltr" className="text-gold ms-1">{p.code}</span>
                              </div>
                            ))}
                            {item.perfumes.length > 2 && (
                              <div className="text-muted-foreground">
                                {interpolate(t("cart.more"), { count: item.perfumes.length - 2 })}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Per-Item Price Breakdown: Gross → Ticket Discount → Net */}
                        {(() => {
                          const itemGrossPrice = getItemUnitPrice(item) * (item.quantity || 1);
                          const itemCategory = item.packageId ? packagingCategories[item.packageId] : null;
                          const isAccessory = itemCategory === 'accessory' || !!(item as any).isSample;

                          // Calculate ticket credit for this item if applicable
                          const availableTickets = transactions
                            .filter(tx => tx.transaction_type === 'credit')
                            .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                            .map(tx => ({ amount_aed: tx.amount_aed, id: tx.id }));

                          let ticketDiscount = 0;
                          if (availableTickets.length > 0 && selectedItemIds.has(item.id) && !isAccessory && creditBalance > 0) {
                            // Calculate how many unit-tickets were consumed by previous eligible items
                            const selectedItems = cartItems.filter(i => selectedItemIds.has(i.id));
                            let unitsBefore = 0;
                            for (const si of selectedItems) {
                              const siCategory = si.packageId ? packagingCategories[si.packageId] : null;
                              const siIsAccessory = siCategory === 'accessory' || !!(si as any).isSample;
                              if (si.id === item.id) break;
                              if (!siIsAccessory) unitsBefore += (si.quantity || 1);
                            }
                            // Apply one ticket per unit for this item's quantity
                            const qty = item.quantity || 1;
                            for (let u = 0; u < qty; u++) {
                              const ticketIdx = unitsBefore + u;
                              if (ticketIdx < availableTickets.length) {
                                ticketDiscount += availableTickets[ticketIdx].amount_aed;
                              }
                            }
                          }

                          const itemNetPrice = itemGrossPrice - ticketDiscount;

                          return (
                            <div className="mt-2 pt-2 border-t border-border/50 space-y-1">
                              <div className="flex justify-between text-xs">
                                <span className="text-muted-foreground">{t("cart.gross")}</span>
                                <span className="text-foreground">
                                  <span dir="ltr" className="block"><PriceDisplay amount={itemGrossPrice} packagingOptionId={item.packageId} size="sm" /></span>
                                </span>
                              </div>
                              {ticketDiscount > 0 && (
                                <div className="flex justify-between text-xs">
                                  <span className="text-emerald-600 flex items-center gap-1">
                                    <Wallet className="h-3 w-3" /> {t("cart.credit")}
                                  </span>
                                  <span dir="ltr" className="text-emerald-600">-AED {ticketDiscount.toFixed(2)}</span>
                                </div>
                              )}
                              <div className="flex justify-between text-xs font-semibold">
                                <span className="text-foreground">{t("cart.net")}</span>
                                <span className="text-gold">
                                  <span dir="ltr" className="block"><PriceDisplay amount={itemNetPrice} packagingOptionId={item.packageId} size="sm" /></span>
                                </span>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Photo thumbnail for box orders */}
                        {item.type === "box" && item.photoUrl && (
                          <div className="flex items-center gap-2 mt-2">
                            <img
                              src={item.photoUrl}
                              alt={t("cart.customPhoto")}
                              className="w-10 h-10 rounded-md object-cover border border-gold/30"
                            />
                            <span className="text-xs text-muted-foreground">{t("cart.customPhoto")}</span>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2 mt-3">
                          {/* Only show Edit button for non-collection items */}
                          {!item.isCollectionItem && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditItem(item)}
                              className="h-7 text-xs gap-1 text-green-600 hover:text-green-700 hover:border-green-600"
                            >
                              <Pencil className="h-3 w-3" />
                              {t("cart.edit")}
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteItemId(item.id)}
                            className="h-7 text-xs gap-1 text-red-500 hover:text-red-600 hover:border-red-500"
                          >
                            <Trash2 className="h-3 w-3" />
                            {t("cart.remove")}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Footer */}
          {cartItems.length > 0 && (
            <div className="p-4 border-t border-border bg-background">
              {/* Subtotal */}
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-muted-foreground">
                  {getSelectedCount() === cartItems.length
                    ? t("cart.subtotal")
                    : interpolate(t("cart.selectedSubtotal"), { selected: getSelectedCount(), count: cartItems.length })
                  }
                </span>
                <span dir="ltr" className="block"><PriceDisplay amount={getSubtotal()} size="sm" className="text-foreground" /></span>
              </div>

              {/* Credits line - only show if there are credits to apply */}
              {getCreditRedemption() > 0 && (
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-emerald-600 flex items-center gap-1">
                    <Wallet className="h-3 w-3" />
                    {t("cart.credits")}
                  </span>
                  <span dir="ltr" className="text-sm text-emerald-600">-AED {getCreditRedemption().toFixed(0)}</span>
                </div>
              )}

              {/* Shipping line - show when applicable */}
              {collectionShippingCost > 0 && (
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <Truck className="h-3 w-3" />
                    {t("cart.shipping")}
                  </span>
                  <span dir="ltr" className="block"><PriceDisplay amount={collectionShippingCost} size="sm" className="text-foreground" /></span>
                </div>
              )}
              {collectionShippingCost === 0 && cartItems.some(item => item.isCollectionItem && selectedItemIds.has(item.id)) && (
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <Truck className="h-3 w-3" />
                    {t("cart.shipping")}
                  </span>
                  <span className="text-sm text-emerald-600 font-medium">{t("cart.free")}</span>
                </div>
              )}

              {/* Total */}
              <div className="flex justify-between items-center mb-2 pt-1 border-t border-border/50">
                <span className="font-medium text-foreground">{t("cart.total")}</span>
                <span dir="ltr" className="block"><PriceDisplay amount={getTotalPrice()} size="lg" className="text-gold" /></span>
              </div>

              {getSelectedCount() < cartItems.length && getSelectedCount() > 0 && (
                <p className="text-xs text-muted-foreground mb-3">
                  {t("cart.unselected")}
                </p>
              )}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    const existingCartItems = getCartItems();
                    const hasCollectionItems = existingCartItems.some((item: any) => item.isCollectionItem);
                    const hasQuizItems = existingCartItems.some((item: any) => !item.isCollectionItem);

                    onClose();

                    if (hasCollectionItems && !hasQuizItems) {
                      // Collection-only cart → go to Anaya Collection
                      navigate("/anaya-collection");
                    } else {
                      // Quiz / mixed cart → go to Quiz Step 1
                      const lockedCountry = existingCartItems.length > 0 ? existingCartItems[0].country : null;
                      sessionStorage.setItem("addMoreFlow", "true");
                      if (lockedCountry) {
                        sessionStorage.setItem("addMoreLockedCountry", lockedCountry);
                        sessionStorage.setItem("quizLockedCountry", lockedCountry);
                      }
                      sessionStorage.removeItem("selectedOption");
                      sessionStorage.removeItem("selectedPerfume");
                      sessionStorage.removeItem("perfumes");
                      sessionStorage.removeItem("greetingMessage");
                      sessionStorage.removeItem("photoPreview");
                      sessionStorage.removeItem("quizData");
                      sessionStorage.removeItem("quizCurrentStep");
                      sessionStorage.removeItem("quizFormData");
                      sessionStorage.removeItem("currentOrderId");
                      sessionStorage.setItem("highestPhaseReached", "1");
                      sessionStorage.setItem("startFreshQuiz", "true");
                      navigate("/quiz");
                    }
                  }}
                >
                  {cartItems.every((item: any) => item.isCollectionItem) ? t("cart.addCollection") : t("cart.addMore")}
                </Button>
                <Button
                  variant="gold"
                  className="flex-1"
                  onClick={handleCheckout}
                >
                  {t("cart.checkout")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteItemId} onOpenChange={() => setDeleteItemId(null)}>
        <AlertDialogContent dir={isRTL ? "rtl" : "ltr"} className="z-[80]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("cart.removeTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("cart.confirmRemove")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cart.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteItemId && handleDeleteItem(deleteItemId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("cart.remove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default CartDrawer;
